import {APIResponse} from '@core/interfaces/system/system-common';
import {Observable} from 'rxjs';
import {MaintenancePriority} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';

export enum RuleType {
    AUTOMATIC = 'AUTOMATIC',
    MANUAL = 'MANUAL',
}
export enum LimitationType {
    CONTENT_BASED = 'CONTENT_BASED',
    INSPECTION_BASED = 'INSPECTION_BASED',
}
export enum RuleDiscriminator {
    INSPECTION_APPROVAL = 'INSPECTION_APPROVAL',
    DEFICIENCY = 'DEFICIENCY',
}
export interface Rule {
    id: number;
    ruleOrder: number;
    name: string;
    description: string;
    active: boolean;
    ruleType: RuleType;
    applyTo: RuleDiscriminator;
    ruleActions: InspectionRuleAction | DeficiencyRuleAction;
}
export interface InspectionRuleAction {
    resultingStatus: string;
}
export interface DeficiencyRuleAction {
    description: string;
    externalId: string;
    priority: MaintenancePriority;
    category: string;
    resolved: boolean;
    resolvedComment: string;
}
export interface FullRuleDto {
    id: number;
    name: string;
    description: string;
    ruleType: RuleType;
    ruleOrder: number;
    ruleConditionType: string;
    applyTo: RuleDiscriminator;
    ruleActions: InspectionRuleAction | DeficiencyRuleAction;
    formFieldScopeList: FormFieldScope[];
    inspectionResultScopeList?: InspectionResultScope[];
}
export interface CheckRuleNameExistsRequest {
    ruleName: string;
    applyTo: RuleDiscriminator;
}

export interface FormFieldScope {
    id: number;
    fieldTypes: string[];
    fieldNames: string[];
    filterOperator: string;
    value: string;
    values: string[];
}
export interface InspectionResultScope {
    id: number;
    fieldName: string;
    filterOperator: string;
    value: string;
    values: string[];
}

export interface RuleField {
    key: string;
    name: string;
    formNumber?: number;
}
export interface RuleFilterOptionResult {
    fieldValue: string;
    fieldCount: number;
}
export interface FieldOperatorOption {
    filterOperator: string;
    operatorLabel: string;
    filterType: string;
}

export interface FieldDto {
    fieldName: string;
    fieldKey: string;
    fieldType: string;
    fieldOperatorOptions: FieldOperatorOption[];
    min: number | null;
    max: number | null;
    fieldNumber: number;
}
export interface FieldRequest {
    fieldType?: string;
    fieldName?: string;
    search: string;
    ruleType?: RuleType;
    selectedParam?: string[];
}

export abstract class RulesService {
    //Auto-approve inspection
    abstract getRuleList(applyTo: RuleDiscriminator): Observable<APIResponse<Rule[]>>;
    abstract getRuleById(ruleId: number): Observable<APIResponse<FullRuleDto>>;
    abstract deleteRuleById(ruleId: number): Observable<APIResponse<Rule>>;
    abstract duplicateRuleById(ruleId: number): Observable<APIResponse<FullRuleDto>>;
    abstract editRuleById(rule: FullRuleDto): Observable<APIResponse<FullRuleDto>>;
    abstract createRule(rule: FullRuleDto): Observable<APIResponse<FullRuleDto>>;
    abstract executeRuleById(ruleId: number): Observable<APIResponse<boolean>>;
    abstract toggleRuleStatus(ruleId: number, activeMode: boolean): Observable<APIResponse<any>>;
    abstract getResultAffectedByRule(rule: FullRuleDto): Observable<APIResponse<number>>;
    abstract getFieldTypeList(): Observable<APIResponse<RuleField[]>>;
    abstract getFieldNameListByType(req: FieldRequest): Observable<APIResponse<FieldDto[]>>;
    abstract getFieldValueListByName(param: FieldRequest): Observable<APIResponse<RuleFilterOptionResult[]>>;
    abstract getInspectionNameList(ruleType: RuleType): Observable<APIResponse<FieldDto[]>>;
    abstract getInspectionValueListByName(param: FieldRequest): Observable<APIResponse<any[]>>;
    abstract checkExistingRuleName(req: CheckRuleNameExistsRequest): Observable<APIResponse<boolean>>;
    abstract updateRuleListOrder(ruleList: Rule[]): Observable<APIResponse<boolean>>;
}
